import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { User, UserRole } from '../types';
import { Permission, migrateRoleToPermissions, PermissionGroups } from '../types/permissions.ts';
import PermissionManager from './PermissionManager.tsx';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase/config.ts';
import { 
  Users, 
  UserPlus, 
  Edit3, 
  Trash2, 
  Search, 
  Shield,
  Mail,
  Calendar,
  Save,
  X,
  AlertCircle,
  Key,
  Settings,
  Plus
} from 'lucide-react';

const UserManagement: React.FC = () => {
  const { currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Add user form state
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    displayName: '',
    permissions: [] as Permission[],
    employeeId: '',
    contractorId: '',
    sendEmail: true
  });
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');

  // Check if current user can manage users
  const canManageUsers = hasPermission(Permission.MANAGE_USERS);

  // Fetch users from Firebase
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Handle migration from role-based to permission-based
        let permissions = data.permissions || [];
        if ((!permissions || permissions.length === 0) && data.role) {
          permissions = migrateRoleToPermissions(data.role);
        }
        
        return {
          uid: doc.id,
          email: data.email,
          displayName: data.displayName || '',
          permissions,
          employeeId: data.employeeId,
          contractorId: data.contractorId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          role: data.role, // Keep for migration
        } as User;
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManageUsers) {
      fetchUsers();
    }
  }, [canManageUsers]);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.displayName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Add new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageUsers) return;
    
    try {
      setAddingUser(true);
      setAddUserError('');

      // Generate temporary password
      const tempPassword = Math.random().toString(36).slice(-8) + 'A1!';

      // Create user in Firebase Auth
      const { user } = await createUserWithEmailAndPassword(auth, addUserForm.email, tempPassword);

      // Create user document in Firestore
      const userData = {
        email: addUserForm.email,
        displayName: addUserForm.displayName,
        permissions: addUserForm.permissions,
        employeeId: addUserForm.employeeId || undefined,
        contractorId: addUserForm.contractorId || undefined,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        requiresPasswordReset: true, // Flag to indicate first login
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      // Send password reset email if requested
      if (addUserForm.sendEmail) {
        await sendPasswordResetEmail(auth, addUserForm.email);
      }

      // Reset form
      setAddUserForm({
        email: '',
        displayName: '',
        permissions: [],
        employeeId: '',
        contractorId: '',
        sendEmail: true
      });
      setShowAddUser(false);
      
      // Refresh users list
      await fetchUsers();
      
    } catch (error: any) {
      console.error('Error adding user:', error);
      setAddUserError(error.message);
    } finally {
      setAddingUser(false);
    }
  };

  // Update user
  const handleUpdateUser = async (user: User) => {
    if (!canManageUsers) return;
    
    try {
      setSaving(true);
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        displayName: user.displayName,
        permissions: user.permissions,
        employeeId: user.employeeId || null,
        contractorId: user.contractorId || null,
        updatedAt: serverTimestamp(),
      });
      
      // Refresh users list
      await fetchUsers();
      setEditingUser(null);
      
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: string) => {
    if (!canManageUsers) return;
    
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  // Send password reset email
  const handlePasswordReset = async (email: string) => {
    if (!canManageUsers) return;
    
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent successfully!');
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      alert('Failed to send password reset email: ' + error.message);
    }
  };

  // Get permission summary for display
  const getPermissionSummary = (permissions: Permission[]) => {
    if (permissions.includes(Permission.ADMIN)) {
      return { text: 'Administrator', color: 'bg-red-100 text-red-800' };
    }
    
    const permissionCount = permissions.length;
    if (permissionCount === 0) {
      return { text: 'No permissions', color: 'bg-gray-100 text-gray-800' };
    }
    
    // Check for common permission groups
    const isInvoiceStaff = PermissionGroups.INVOICE_STAFF.every(p => permissions.includes(p));
    const isTimesheetStaff = PermissionGroups.TIMESHEET_STAFF.every(p => permissions.includes(p));
    const isFieldElectrician = PermissionGroups.FIELD_ELECTRICIAN.every(p => permissions.includes(p));
    const isContractor = PermissionGroups.CONTRACTOR.every(p => permissions.includes(p));
    
    if (isInvoiceStaff) {
      return { text: 'Invoice Staff', color: 'bg-indigo-100 text-indigo-800' };
    } else if (isTimesheetStaff) {
      return { text: 'Timesheet Staff', color: 'bg-blue-100 text-blue-800' };
    } else if (isFieldElectrician) {
      return { text: 'Field Electrician', color: 'bg-green-100 text-green-800' };
    } else if (isContractor) {
      return { text: 'Contractor', color: 'bg-purple-100 text-purple-800' };
    }
    
    return { 
      text: `${permissionCount} permission${permissionCount !== 1 ? 's' : ''}`, 
      color: 'bg-gray-100 text-gray-800' 
    };
  };

  if (!canManageUsers) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Users className="mr-3 h-8 w-8 text-brand-gold" />
                User Management
              </h1>
              <p className="text-gray-600 mt-2">
                Manage user accounts and permissions for the 7Twenty4 Management System
              </p>
            </div>
            <button
              onClick={() => setShowAddUser(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-dark hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-gold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            />
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IDs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => {
                    const permissionSummary = getPermissionSummary(user.permissions);
                    
                    return (
                      <tr key={user.uid} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-brand-gold flex items-center justify-center">
                                <span className="text-sm font-medium text-brand-dark">
                                  {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName || 'No name'}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${permissionSummary.color}`}>
                            {permissionSummary.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.employeeId && (
                            <div>Employee: {user.employeeId}</div>
                          )}
                          {user.contractorId && (
                            <div>Contractor: {user.contractorId}</div>
                          )}
                          {!user.employeeId && !user.contractorId && (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.createdAt.toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => setEditingUser(user)}
                            className="text-brand-dark hover:text-brand-gold transition-colors duration-200"
                            title="Edit User"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handlePasswordReset(user.email)}
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          {user.uid !== currentUser?.uid && (
                            <button
                              onClick={() => handleDeleteUser(user.uid)}
                              className="text-red-600 hover:text-red-800 transition-colors duration-200"
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add User Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <UserPlus className="mr-2 h-5 w-5" />
                  Add New User
                </h2>
                <button
                  onClick={() => setShowAddUser(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={addUserForm.email}
                      onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={addUserForm.displayName}
                      onChange={(e) => setAddUserForm({ ...addUserForm, displayName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={addUserForm.employeeId}
                      onChange={(e) => setAddUserForm({ ...addUserForm, employeeId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contractor ID
                    </label>
                    <input
                      type="text"
                      value={addUserForm.contractorId}
                      onChange={(e) => setAddUserForm({ ...addUserForm, contractorId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={addUserForm.sendEmail}
                      onChange={(e) => setAddUserForm({ ...addUserForm, sendEmail: e.target.checked })}
                      className="rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Send password reset email to user
                    </span>
                  </label>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Settings className="mr-2 h-5 w-5" />
                    User Permissions
                  </h3>
                  <PermissionManager
                    selectedPermissions={addUserForm.permissions}
                    onPermissionsChange={(permissions) => setAddUserForm({ ...addUserForm, permissions })}
                    showPresets={true}
                    disabled={addingUser}
                  />
                </div>

                {addUserError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
                      <span className="text-red-800">{addUserError}</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddUser(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingUser}
                    className="px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {addingUser ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Edit3 className="mr-2 h-5 w-5" />
                  Edit User: {editingUser.displayName}
                </h2>
                <button
                  onClick={() => setEditingUser(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={editingUser.displayName || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={editingUser.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={editingUser.employeeId || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, employeeId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contractor ID
                    </label>
                    <input
                      type="text"
                      value={editingUser.contractorId || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, contractorId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Settings className="mr-2 h-5 w-5" />
                    User Permissions
                  </h3>
                  <PermissionManager
                    selectedPermissions={editingUser.permissions}
                    onPermissionsChange={(permissions) => setEditingUser({ ...editingUser, permissions })}
                    showPresets={true}
                    disabled={saving}
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdateUser(editingUser)}
                    disabled={saving}
                    className="px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;